import { z } from 'zod';
import type { CommissioningThresholdSource } from '../types/commissioning';
import { commissioningThresholdParametersSchema } from './commissioning-threshold-economics';

/**
 * VALORI del configuratore Soglie per livello (istanza, cluster, cella driver,
 * membro), keyed sulle identità stabili della struttura del template.
 *
 * Ogni livello salva SOLO i valori che specializza: un campo assente eredita
 * dal livello meno specifico (membro ← cella ← cluster ← istanza). La
 * completezza si valuta sul risultato composto, mai sul singolo livello.
 */
export const commissioningThresholdBandValuesSchema = z.object({
  /** Estremo inferiore della banda (unità dell'indicatore). */
  from: z.number().finite().optional(),
  /** Estremo superiore escluso; assente = banda aperta (solo l'ultima). */
  to: z.number().finite().optional(),
  /** Tolleranza % di ingresso banda (0–99). */
  tolerancePct: z.number().min(0).max(99).optional(),
  canoneMultiplier: z.number().finite().nonnegative().optional(),
  gettoneGaraMultiplier: z.number().finite().nonnegative().optional(),
  perUnitEur: z.number().finite().optional(),
  flatEur: z.number().finite().nonnegative().optional(),
  /** Parametri delle categorie della banda (chiave = id categoria). */
  categories: z.record(z.string().uuid(), commissioningThresholdParametersSchema).optional(),
  /** Delta delle maggiorazioni della banda (chiave = id maggiorazione). */
  surcharges: z.record(z.string().uuid(), commissioningThresholdParametersSchema).optional(),
}).strict();
export type CommissioningThresholdBandValues = z.infer<typeof commissioningThresholdBandValuesSchema>;

export const commissioningThresholdValuesSchema = z.object({
  /** Valori per banda (chiave = id banda della struttura del template). */
  bands: z.record(z.string().uuid(), commissioningThresholdBandValuesSchema),
}).strict().superRefine((values, ctx) => {
  if (Object.keys(values.bands).length > 100) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['bands'], message: 'Sono ammesse al massimo 100 bande',
  });
});
export type CommissioningThresholdValues = z.infer<typeof commissioningThresholdValuesSchema>;

/** Campo valore associato a ogni fonte dichiarabile. */
export const COMMISSIONING_THRESHOLD_SOURCE_FIELD = {
  canone_multiplier: 'canoneMultiplier',
  gettone_gara_multiplier: 'gettoneGaraMultiplier',
  per_unit_eur: 'perUnitEur',
  flat_eur: 'flatEur',
} as const satisfies Record<CommissioningThresholdSource, keyof CommissioningThresholdBandValues>;
export type CommissioningThresholdSourceField =
  typeof COMMISSIONING_THRESHOLD_SOURCE_FIELD[CommissioningThresholdSource];

/** Etichette operative delle fonti (una sola fonte di verità per BE, FE e documenti). */
export const COMMISSIONING_THRESHOLD_SOURCE_LABEL: Record<CommissioningThresholdSource, string> = {
  canone_multiplier: 'Moltiplicatore canone',
  gettone_gara_multiplier: 'Moltiplicatore gettone gara',
  per_unit_eur: '€ per unità',
  flat_eur: 'Premio flat €',
};
